"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import {
  assertOfflineContext,
  offlineContext,
  OFFLINE_ACCOUNT_EVENT,
} from "@/lib/offline/context";
import type { LibraryContext } from "@/native-shell/library";

export function useFeedbackAccount() {
  const [context, setContext] = useState<LibraryContext | null>(null),
    [error, setError] = useState("");
  useEffect(() => {
    let alive = true,
      version = 0;
    const refresh = () => {
      const current = ++version;
      setContext(null);
      setError("");
      void offlineContext()
        .then((c) => {
          if (alive && version === current) setContext(c);
        })
        .catch(() => {
          if (alive && version === current)
            setError(
              "Your account could not be checked. Reopen this page when connected.",
            );
        });
    };
    refresh();
    window.addEventListener(OFFLINE_ACCOUNT_EVENT, refresh);
    return () => {
      alive = false;
      window.removeEventListener(OFFLINE_ACCOUNT_EVENT, refresh);
    };
  }, []);
  return { context, error };
}

/** A late response can never populate a different account or an unmounted form. */
export function useFeedbackAction(context: LibraryContext) {
  const [busy, setBusy] = useState(false),
    [message, setMessage] = useState("");
  const alive = useRef(true),
    inFlight = useRef(false);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const run = useCallback(
    async <T,>(work: () => Promise<T>, apply: (result: T) => void) => {
      if (inFlight.current) return;
      inFlight.current = true;
      setBusy(true);
      setMessage("");
      try {
        await assertOfflineContext(context);
        const result = await work();
        await assertOfflineContext(context);
        if (alive.current) apply(result);
      } catch (error) {
        try {
          await assertOfflineContext(context);
          if (alive.current)
            setMessage(
              error instanceof Error
                ? error.message
                : "Your changes could not be confirmed. Keep this view open and retry.",
            );
        } catch {
          /* The owning view hides itself on account changes. */
        }
      } finally {
        inFlight.current = false;
        if (alive.current) setBusy(false);
      }
    },
    [context],
  );
  return { busy, message, setMessage, run };
}

export const feedbackButton =
  "inline-flex min-h-11 items-center justify-center rounded-full border border-border bg-white px-4 py-2 text-sm font-semibold text-navy hover:border-teal disabled:opacity-50";
